import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Download, Loader2 } from "lucide-react";

const initial = { name: "", email: "", company: "", access_code: "" };

export default function BrandKitAccess() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Use raw fetch so we can receive a binary blob (axios is JSON-by-default here)
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${baseUrl}/api/brand-kit/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      // Trigger download in browser
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "WealthFirstInc_BrandKit.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDone(true);
      toast.success("Brand kit downloaded.");
    } catch (err) {
      toast.error(err.message || "Could not unlock the brand kit.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[760px] mx-auto px-6 lg:px-10 pt-32 pb-24" data-testid="brand-kit-access-page">
      <div className="mb-12 reveal">
        <p className="eyebrow mb-4">Brand Kit · Manufacturer Access</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6 leading-[0.95]">
          For our <em style={{ color: "var(--brass)" }}>production</em> partners.
        </h1>
        <p className="lede max-w-2xl">
          The full WealthFirst Brand Kit zip — vector logo files, color tokens, type system, brand-guidelines
          PDF, and the production-reference apparel mockups (socks, T-shirt, cap, branded waistband).
          Gated by a shared access code. Enter your details and the code we sent you to begin the download.
        </p>
      </div>

      {!done && (
        <form onSubmit={submit} className="space-y-6" data-testid="brand-kit-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="eyebrow block mb-2">Contact name *</label>
              <input
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="input-line"
                placeholder="e.g. Jane Tailor"
                data-testid="brand-kit-name"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Email *</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="input-line"
                placeholder="jane@your-factory.com"
                data-testid="brand-kit-email"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">Company / Factory name *</label>
            <input
              required
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              className="input-line"
              placeholder="e.g. Riccardo Luxury Embroidery, Como, Italy"
              data-testid="brand-kit-company"
            />
          </div>

          <div>
            <label className="eyebrow block mb-2">Manufacturer access code *</label>
            <input
              required
              value={form.access_code}
              onChange={(e) => set("access_code", e.target.value)}
              className="input-line"
              placeholder="Issued by WealthFirst — case-sensitive"
              data-testid="brand-kit-access-code"
              autoComplete="off"
              spellCheck="false"
            />
            <p className="text-[11px] mt-2" style={{ color: "var(--ink-faint)" }}>
              Don&apos;t have an access code?{" "}
              <a href="mailto:support@wealthfirstinc.tv?subject=Brand-Kit%20Access%20Request" className="brass-link">
                Request one from support@wealthfirstinc.tv ↗
              </a>
            </p>
          </div>

          <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--ink-faint)" }}>
              <ShieldCheck size={14} style={{ color: "var(--brass)" }} />
              Server-side validation · Each download logged for audit.
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-brass"
              style={{ minWidth: "240px", justifyContent: "center" }}
              data-testid="brand-kit-submit"
            >
              {busy ? (
                <><Loader2 size={14} className="animate-spin mr-2" /> Verifying…</>
              ) : (
                <><Download size={14} className="mr-2" /> Unlock & Download Kit</>
              )}
            </button>
          </div>
        </form>
      )}

      {done && (
        <div className="p-10 text-center" style={{ border: "1px solid var(--brass)", background: "var(--surface)" }} data-testid="brand-kit-done">
          <ShieldCheck size={44} className="mx-auto mb-6" style={{ color: "var(--brass)" }} />
          <p className="eyebrow mb-3">Download Complete</p>
          <h2 className="font-display text-4xl leading-tight mb-4">
            The kit is on its way to <em style={{ color: "var(--brass)" }}>{form.company}</em>.
          </h2>
          <p className="text-base mb-8 max-w-lg mx-auto" style={{ color: "var(--ink-muted)" }}>
            A confirmation email has been sent to {form.email}. If you need updated assets, embroidery files,
            or additional mockups, reply to that email and we&apos;ll deliver them.
          </p>
          <button
            onClick={() => { setForm(initial); setDone(false); }}
            className="text-xs uppercase tracking-[0.18em] px-5 py-2"
            style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink-muted)" }}
          >
            Download for another partner →
          </button>
        </div>
      )}

      <hr className="hairline my-16" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm" data-testid="brand-kit-extras">
        <div className="p-6" style={{ border: "1px solid var(--hairline)" }}>
          <p className="eyebrow mb-3">Already a Licensee?</p>
          <p className="mb-4" style={{ color: "var(--ink-muted)" }}>
            Sign in to your licensee dashboard for direct downloads — no access code needed.
          </p>
          <Link to="/license/dashboard" className="brass-link text-xs uppercase tracking-[0.18em]">
            Open Licensee Dashboard →
          </Link>
        </div>
        <div className="p-6" style={{ border: "1px solid var(--hairline)" }}>
          <p className="eyebrow mb-3">Preview the Marks</p>
          <p className="mb-4" style={{ color: "var(--ink-muted)" }}>
            The public brand-kit reference — logo, palette, typography, usage rules.
          </p>
          <a href="/logo.html" target="_blank" rel="noreferrer" className="brass-link text-xs uppercase tracking-[0.18em]">
            Open /logo.html ↗
          </a>
        </div>
        <div className="p-6" style={{ border: "1px solid var(--hairline)" }}>
          <p className="eyebrow mb-3">License the Brand</p>
          <p className="mb-4" style={{ color: "var(--ink-muted)" }}>
            $4,500/yr recurring annual brand license — includes all assets + concierge compliance review.
          </p>
          <Link to="/license" className="brass-link text-xs uppercase tracking-[0.18em]">
            Apply for License →
          </Link>
        </div>
      </div>
    </div>
  );
}
